import { createFunction, getCustomTag, processCondition, useTranslation } from '@msdk/common'
import {
  CustomActionButtonAspect,
  CustomActionDefinition,
  EntityOperation,
  FormNode,
  LogicalPath,
  UserPath,
} from '@msdk/definitions'
import { ButtonAlternative, ButtonPrimary, ButtonSecondary, IconSelector, styled } from '@msdk/ui'
import React from 'react'

import { useEntityFromCollection } from '../../state'
import { ActionVisualizationMode } from './action-visualization'
import { ActionMenuItem, ActionMenuText } from './ActionMenuItem'

const StyledButtonPrimary = styled(ButtonPrimary)`
  margin-left: 5px;
`

const StyledButtonSecondary = styled(ButtonSecondary)`
  margin-left: 5px;
`

const StyledButtonAlternative = styled(ButtonAlternative)`
  margin-left: 5px;
`

interface Props {
  action: CustomActionDefinition
  entityId: string
  formNode: FormNode
  visualizationMode: ActionVisualizationMode
  save: (actionId?: string) => Promise<EntityOperation>
  handleClose?: () => void
}

export function FormActionButton(props: Props) {
  const t = useTranslation()
  const entityContext = useEntityFromCollection(props.entityId)

  const handleClick = async () => {
    await props.save(props.action.name)
    props.handleClose && props.handleClose()
  }

  const isDisabled = () => {
    if (props.action.canExecute && entityContext) {
      const func = createFunction(processCondition(props.action.canExecute))
      return !evaluate(func)
    }
    return false
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  const evaluate = (func: Function) => {
    if (entityContext) {
      const res = func({ entity: entityContext, row: undefined, logic: new LogicalPath(), user: new UserPath() })
      return res
    }
    return false
  }

  const ButtonComponent =
    props.action.buttonAspect === CustomActionButtonAspect.Alternative
      ? StyledButtonAlternative
      : props.action.buttonAspect === CustomActionButtonAspect.Secondary
      ? StyledButtonSecondary
      : StyledButtonPrimary

  return props.visualizationMode === 'Buttons' ? (
    <ButtonComponent
      disabled={isDisabled()}
      onClick={handleClick}
      key={props.action.name}
      data-test-id={getCustomTag(['form', props.action.description, 'button'])}
    >
      {props.action.icon && <IconSelector iconName={props.action.icon} />}
      {t(props.action.idml, props.action.description)}
    </ButtonComponent>
  ) : (
    <ActionMenuItem
      onClick={handleClick}
      disabled={isDisabled()}
      aspect={props.action.buttonAspect}
      data-test-id={getCustomTag(['form', props.action.description, 'button'])}
    >
      {props.action.icon && <IconSelector iconName={props.action.icon} />}
      <ActionMenuText disabled={isDisabled()} aspect={props.action.buttonAspect}>
        {t(props.action.idml, props.action.description)}
      </ActionMenuText>
    </ActionMenuItem>
  )
}
